import { ChangeEvent, useEffect, useState } from "react";
import { Accordion, AccordionHeader, Button, Form, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Organisation } from "../../../../models/Organisation";
import { PermissionAction, User, UserGroup } from "../../../../models/User";
import { ApplicationState } from "../../../../store";
import * as GeneralStore from "../../../../store/general/General";
import { useAddProjectMutation, useGetUserGroupsQuery, useGetUsersQuery } from "../../../../store/api/kinesense";
import { capitaliseFirst } from "../../../../utilities/helpers";
import { Notifications } from "../../../../utilities/Notifications/Notifications";
import SpinnerButton from "../../../../components/SpinnerButton";

export interface AddProjectModalProps {
    isVisible: boolean;
    setIsVisible: (nextIsVisible: boolean) => void;
}

const defaultPermissionActions: PermissionAction[] = ["peek", "read", "update", "manage", "delete"];
const allPermissionActions: PermissionAction[] = ["peek", "read", "update", "manage", "delete"];

type PermissionMap = Map<string, PermissionAction[]>;

export function AddProjectModal(props: AddProjectModalProps) {
    const dispatch = useDispatch();
    const user = useSelector((state: ApplicationState) => state.user);
    const userCanViewOtherUsers =
        User.can(user.permissions.permissions, "read", "users") ||
        User.can(user.permissions.permissions, "peek", "users");

    const { data: userGroups } = useGetUserGroupsQuery();
    const { data: users } = useGetUsersQuery({}, { skip: !userCanViewOtherUsers });

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isValid, setIsValid] = useState(false);
    const [allowedGroups, setAllowedGroups] = useState<UserGroup[]>([]);
    const [allowedUsers, setAllowedUsers] = useState<User[]>([]);
    const [groupPermissions, setGroupPermissions] = useState<PermissionMap>(new Map<string, PermissionAction[]>());
    const [userPermissions, setUserPermissions] = useState<PermissionMap>(new Map<string, PermissionAction[]>());
    const [isSavingProject, setIsSavingProject] = useState(false);

    const [requestAddProject] = useAddProjectMutation();

    useEffect(() => {
        if (userGroups != undefined && userGroups.length > 0) {
            setAllowedGroups([userGroups.find((group) => group.isBasic)]);
            const basicUserGroupId = Organisation.createOrganisationBasicUserGroupId(userGroups[0].organisationId);
            setGroupPermissions((prev) => new Map(prev).set(basicUserGroupId, defaultPermissionActions));
        }
    }, [userGroups]);

    useEffect(() => {
        setIsValid(name !== "");
    }, [name, isValid]);

    const hideModal = () => {
        props.setIsVisible(false);
    };

    const handleAllowedGroupsChange = (selectedOptions: { value: string; label: string }[]) => {
        if (userGroups) {
            const selectedGroups = userGroups.filter((group) =>
                selectedOptions.some((option) => option.value === group.groupId),
            );

            setGroupPermissions(() => {
                const newMap = new Map<string, PermissionAction[]>();
                selectedGroups.forEach((group) => {
                    if (!newMap.has(group.groupId)) {
                        newMap.set(group.groupId, defaultPermissionActions);
                    }
                });

                return newMap;
            });

            setAllowedGroups(selectedGroups);
        }
    };

    const handleAllowedUsersChange = (selectedOptions: { value: string; label: string }[]) => {
        if (users) {
            const selectedUsers = users.filter((user) =>
                selectedOptions.some((option) => option.value === user.username),
            );

            setUserPermissions(() => {
                const newMap = new Map<string, PermissionAction[]>();
                selectedUsers.forEach((user) => {
                    if (!newMap.has(user.username)) {
                        newMap.set(user.username, defaultPermissionActions);
                    }
                });

                return newMap;
            });

            setAllowedUsers(selectedUsers);
        }
    };

    const handlePermissionChange = (
        input: ChangeEvent<HTMLInputElement>,
        groupId: string,
        action: PermissionAction,
        stateSetter: (updater: (prev: PermissionMap) => PermissionMap) => void,
    ) => {
        const isChecked = input.target.checked;
        stateSetter((prev) => {
            const perms = [...(prev.get(groupId) || [])];

            if (isChecked) {
                perms.push(action);

                if (action == "read" && !perms.includes("peek")) {
                    perms.push("peek");
                }

                if (action == "update" || (action == "delete" && !perms.includes("read"))) {
                    perms.push("read");
                    perms.push("peek");
                }
            } else {
                const index = perms.indexOf(action);
                if (index > -1) {
                    perms.splice(index, 1);
                }
            }

            const newMap = new Map(prev).set(groupId, perms);
            return newMap;
        });
    };

    const saveNewProject = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (name == "") {
            return;
        }

        setIsSavingProject(true);

        const newProject = {
            name,
            description,
        };

        const permissions = {
            groups: Object.fromEntries(allowedGroups.map((g) => [g.groupId, groupPermissions.get(g.groupId) || []])),
            users: Object.fromEntries(allowedUsers.map((u) => [u.username, userPermissions.get(u.username) || []])),
        };

        if (userGroups.length == 0) {
            permissions.groups = {
                [Organisation.createOrganisationBasicUserGroupId(user.organisationId)]: ["read", "update"],
            };
        }

        requestAddProject({ project: newProject, permissions })
            .unwrap()
            .then((createdProject) => {
                Notifications.notify(
                    "New project created",
                    `A new project with name '${name}' has been successfully created`,
                );
                if (createdProject?.projectId) {
                    dispatch(GeneralStore.actionCreators.setActiveProject(createdProject.projectId));
                }
                hideModal();
            })
            .catch((error) => {
                Notifications.notify(
                    "Error creating project",
                    `The following error was encountered while attempting to create a new project: ${
                        error?.data?.message ?? error?.message ?? error?.error ?? "Unknown error"
                    }`,
                );
            })
            .finally(() => {
                setIsSavingProject(false);
            });
    };

    const groupPermissionsAccordion = (
        <Accordion className="permissions-accordion">
            <Accordion.Item eventKey="0">
                <Accordion.Header>Detailed Group Permissions</Accordion.Header>
                <Accordion.Body>
                    <table style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                <th style={{ width: "100%" }}>Group</th>
                                {allPermissionActions.map((action) => (
                                    <th key={action} style={{ textAlign: "center", padding: "0 0.5rem" }}>
                                        {capitaliseFirst(action)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {allowedGroups.map((group) => {
                                return (
                                    <tr key={group.groupId}>
                                        <td>{group.name}</td>

                                        {allPermissionActions.map((action) => (
                                            <PermissionCell
                                                key={action}
                                                action={action}
                                                permissions={groupPermissions}
                                                id={group.groupId}
                                                changed={(input) =>
                                                    handlePermissionChange(
                                                        input,
                                                        group.groupId,
                                                        action,
                                                        setGroupPermissions,
                                                    )
                                                }
                                            />
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );

    const userPermissionsAccordion = (
        <Accordion className="permissions-accordion">
            <Accordion.Item eventKey="0">
                <Accordion.Header>Detailed User Permissions</Accordion.Header>
                <Accordion.Body>
                    <table style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                <th style={{ width: "30%" }}>User</th>
                                {allPermissionActions.map((action) => (
                                    <th key={action} style={{ textAlign: "center", padding: "0 0.5rem" }}>
                                        {capitaliseFirst(action)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {allowedUsers.map((user) => {
                                return (
                                    <tr key={user.username}>
                                        <td>{user.username}</td>

                                        {allPermissionActions.map((action) => (
                                            <PermissionCell
                                                key={action}
                                                action={action}
                                                permissions={userPermissions}
                                                id={user.username}
                                                changed={(input) =>
                                                    handlePermissionChange(
                                                        input,
                                                        user.username,
                                                        action,
                                                        setUserPermissions,
                                                    )
                                                }
                                            />
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );

    return (
        <Modal
            id="add-project-modal"
            show={props.isVisible}
            onHide={hideModal}
            size="lg"
            centered
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>New Project</Modal.Title>
            </Modal.Header>

            <Form className="new-resource-form" onSubmit={saveNewProject}>
                <Modal.Body>
                    <Form.Group className="group">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            name="projectname"
                            type="text"
                            maxLength={32}
                            value={name || ""}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Project Name"
                            required
                        />
                    </Form.Group>

                    <Form.Group className="group">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            type="text"
                            maxLength={512}
                            value={description || ""}
                            style={{ minHeight: "8em" }}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Project Description"
                        />
                    </Form.Group>

                    <Accordion className="group permissions-accordion">
                        <Accordion.Item eventKey="gperms">
                            <Accordion.Header>Group Permissions</Accordion.Header>
                            <Accordion.Body>
                                {allowedGroups && userGroups && userGroups.length > 0 && (
                                    <table className="form-table">
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {userGroups && (
                                                        <Select
                                                            value={allowedGroups.map((group) => ({
                                                                value: group.groupId,
                                                                label: group.name,
                                                            }))}
                                                            options={userGroups.map((group) => ({
                                                                value: group.groupId,
                                                                label: group.name,
                                                            }))}
                                                            classNamePrefix="react-select"
                                                            className="target-select"
                                                            placeholder="Select permitted groups..."
                                                            isMulti
                                                            onChange={handleAllowedGroupsChange}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{groupPermissionsAccordion}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                )}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>

                    <Accordion className="group permissions-accordion">
                        <Accordion.Item eventKey="uperms">
                            <AccordionHeader>User Permissions</AccordionHeader>
                            <Accordion.Body>
                                <table className="form-table">
                                    <tbody>
                                        <tr>
                                            <td>
                                                {users && (
                                                    <Select
                                                        value={allowedUsers.map((user) => ({
                                                            value: user.username,
                                                            label: user.username,
                                                        }))}
                                                        options={users.map((user) => ({
                                                            value: user.username,
                                                            label: user.username,
                                                        }))}
                                                        classNamePrefix="react-select"
                                                        placeholder="Select permitted users..."
                                                        isMulti
                                                        onChange={handleAllowedUsersChange}
                                                    />
                                                )}
                                            </td>
                                        </tr>

                                        <tr>
                                            <td>{userPermissionsAccordion}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={hideModal}>
                        Cancel
                    </Button>
                    <SpinnerButton
                        isLoading={isSavingProject}
                        label="Create project"
                        buttonProps={{ type: "submit", variant: "success", disabled: !isValid }}
                    />
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export function PermissionCell(props: {
    action: PermissionAction;
    permissions: PermissionMap;
    changed: (input: ChangeEvent<HTMLInputElement>) => void;
    id: string;
}) {
    const { action, permissions, changed, id } = props;
    const can = (action: PermissionAction) => (permissions.get(id) || []).includes(action);

    let forceDisabled = (action == "peek" || action == "read") && (can("update") || can("delete") || can("manage"));
    forceDisabled ||= action == "peek" && can("read");

    return (
        <td style={{ textAlign: "center" }}>
            <Form.Check
                type="checkbox"
                id={`permission-${action}-${id}`}
                name={`permission-${action}-${id}`}
                disabled={forceDisabled}
                checked={permissions.get(id)?.includes(action)}
                onChange={(event) => changed(event)}
            />
        </td>
    );
}
